'use client';

import { FlywayFilterCarousel } from '@/components/patterns/FlywayFilterCarousel';
import { OutboundTextLink } from '@/components/patterns/OutboundTextLink';
import { GA_LINK_PLACEMENT } from '@/constants/analytics';
import {
  RED_LIST_INDEX_COMPARISON_FILTER_ID,
  RED_LIST_INDEX_FILTERS,
  RED_LIST_INDEX_FLYWAYS,
  STATE_FLYWAY_COLORS,
  STATE_FLYWAY_FILTERS,
  type RedListIndexFilterId,
  type RedListIndexFlywayId,
} from '@/constants/stateCharts';

import styles from './StateChartControls.module.css';

import type { Messages } from '@/content/messages';

type RedListIndexFiltersProps = {
  heading: string;
  charts: Messages['state']['charts'];
  flywayFilter: RedListIndexFilterId;
  onFlywayFilterChange: (filter: RedListIndexFilterId) => void;
};

function isFlywayFilter(filter: RedListIndexFilterId): filter is RedListIndexFlywayId {
  return RED_LIST_INDEX_FLYWAYS.some((flywayId) => flywayId === filter);
}

function carouselColors(filter: RedListIndexFilterId) {
  if (!isFlywayFilter(filter)) {
    return {
      background: 'var(--color-black-3)',
      foreground: 'var(--text-primary-inverse)',
    };
  }

  return {
    background: STATE_FLYWAY_COLORS[filter],
    foreground: 'var(--color-ink)',
  };
}

function filterLabel(
  charts: Messages['state']['charts'],
  filterId: RedListIndexFilterId,
): string {
  if (filterId === 'all') {
    return charts.redListIndex.allFlywaysLabel;
  }

  if (filterId === RED_LIST_INDEX_COMPARISON_FILTER_ID) {
    return charts.redListIndex.showAllLabel;
  }

  const labelIndex = STATE_FLYWAY_FILTERS.indexOf(filterId);
  return charts.filters[labelIndex] ?? filterId;
}

function carouselLabels(charts: Messages['state']['charts']): string[] {
  return RED_LIST_INDEX_FILTERS.map((filterId) => {
    return filterLabel(charts, filterId);
  });
}

/**
 * Flyway-only filter panel for the Red List Index chart (no View-by).
 * Renders controls stack: heading panel + sticky carousel (desktop tabs inside
 * panel). Parent sticky scope must wrap this + the chart column.
 */
export function RedListIndexFilters({
  heading,
  charts,
  flywayFilter,
  onFlywayFilterChange,
}: RedListIndexFiltersProps) {
  return (
    <div className={styles.controlsStack}>
      <div className={styles.panel}>
        <h3 className={styles.heading}>{heading}</h3>

        <div className={styles.desktopFilters}>
          <div className={styles.group}>
            <p className={styles.label}>{charts.filterByLabel}</p>
            <ul
              className={styles.filterList}
              role="group"
              aria-label={charts.filterByLabel}
            >
              {RED_LIST_INDEX_FILTERS.map((filterId) => {
                const label = filterLabel(charts, filterId);
                const isActive = flywayFilter === filterId;
                const flywayColor = isFlywayFilter(filterId)
                  ? STATE_FLYWAY_COLORS[filterId]
                  : undefined;

                return (
                  <li key={filterId}>
                    <button
                      type="button"
                      className={isActive ? styles.filterTabActive : styles.filterTab}
                      aria-pressed={isActive}
                      style={
                        isActive && flywayColor
                          ? {
                              backgroundColor: flywayColor,
                              color: 'var(--color-ink)',
                            }
                          : undefined
                      }
                      onClick={() => onFlywayFilterChange(filterId)}
                    >
                      {label}
                    </button>
                  </li>
                );
              })}
            </ul>
          </div>

          <OutboundTextLink
            href={charts.redListIndex.datazoneUrl}
            className={styles.datazoneLink}
            linkPlacement={GA_LINK_PLACEMENT.datazoneChart}
          >
            {charts.datazoneLink}
          </OutboundTextLink>
        </div>
      </div>

      <FlywayFilterCarousel
        filters={RED_LIST_INDEX_FILTERS}
        value={flywayFilter}
        onChange={onFlywayFilterChange}
        labels={carouselLabels(charts)}
        previousLabel={charts.previousFlyway}
        nextLabel={charts.nextFlyway}
        getColors={carouselColors}
        variant="inverse"
        className={styles.carousel}
      />
    </div>
  );
}
